import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _Icon;
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useConvertToMobile } from '../../utils/useConvertToMobile';
import { useElementDimensions } from '../../utils';
import { Icon } from '../../Icon';
import { Button } from '../../Button/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { CONTAINER_MAX_WIDTH } from '../alert.consts';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ICON_WIDTH = 20;
var ICON_MARGIN = 16;
var HEADER_MARGIN = 6;
var BODY_MARGIN = 12;
var PRIMARY_BUTTON_MARGIN = 12;
var SECONDARY_BUTTON_MARGIN = 8;
var CLOSE_BUTTON_WIDTH = 20;
var ELEMENT_MARGINS = ICON_WIDTH + ICON_MARGIN + HEADER_MARGIN + BODY_MARGIN + PRIMARY_BUTTON_MARGIN + SECONDARY_BUTTON_MARGIN + CLOSE_BUTTON_WIDTH;
export function FullWidthAlert(props) {
  var _Button, _Box;
  var _props$body = props.body,
    body = _props$body === void 0 ? '' : _props$body,
    _props$color = props.color,
    color = _props$color === void 0 ? 'default' : _props$color,
    handleFullWidthPaddingChange = props.handleFullWidthPaddingChange,
    _props$header = props.header,
    header = _props$header === void 0 ? '' : _props$header,
    _props$hidePrimaryBut = props.hidePrimaryButton,
    hidePrimaryButton = _props$hidePrimaryBut === void 0 ? false : _props$hidePrimaryBut,
    _props$hideSecondaryB = props.hideSecondaryButton,
    hideSecondaryButton = _props$hideSecondaryB === void 0 ? false : _props$hideSecondaryB,
    _props$hideXCloseButt = props.hideXCloseButton,
    hideXCloseButton = _props$hideXCloseButt === void 0 ? false : _props$hideXCloseButt,
    _props$icon = props.icon,
    icon = _props$icon === void 0 ? 'InfoCircle' : _props$icon,
    mobileBreakpoint = props.mobileBreakpoint,
    mobileBreakpointContainerRef = props.mobileBreakpointContainerRef,
    onClose = props.onClose,
    onPrimaryButtonClick = props.onPrimaryButtonClick,
    onSecondaryButtonClick = props.onSecondaryButtonClick,
    primaryButtonContent = props.primaryButtonContent,
    primaryButtonEndIcon = props.primaryButtonEndIcon,
    primaryButtonStartIcon = props.primaryButtonStartIcon,
    primaryButtonStyles = props.primaryButtonStyles,
    secondaryButtonContent = props.secondaryButtonContent,
    secondaryButtonEndIcon = props.secondaryButtonEndIcon,
    secondaryButtonStartIcon = props.secondaryButtonStartIcon,
    secondaryButtonStyles = props.secondaryButtonStyles;
  var isMobile = useConvertToMobile(mobileBreakpoint, mobileBreakpointContainerRef);
  var closeButton = useMemo(function () {
    return _Button || (_Button = /*#__PURE__*/_jsx(Button, {
      onClick: onClose,
      variant: "link-gray",
      children: _Icon || (_Icon = /*#__PURE__*/_jsx(Icon, {
        iconId: "XClose"
      }))
    }));
  }, [onClose]);
  var innerContainerRef = useRef(null);
  var _useElementDimensions = useElementDimensions(innerContainerRef),
    innerContainerWidth = _useElementDimensions.width;
  var primaryButtonRef = useRef(null);
  var secondaryButtonRef = useRef(null);
  var headerRef = useRef(null);
  var bodyRef = useRef(null);
  var _useElementDimensions2 = useElementDimensions(primaryButtonRef),
    primaryButtonWidth = _useElementDimensions2.width;
  var _useElementDimensions3 = useElementDimensions(secondaryButtonRef),
    secondaryButtonWidth = _useElementDimensions3.width;
  var _useElementDimensions4 = useElementDimensions(headerRef),
    headerWidth = _useElementDimensions4.width;
  var _useElementDimensions5 = useElementDimensions(bodyRef),
    bodyWidth = _useElementDimensions5.width;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    elementWidths = _useState2[0],
    setElementWidths = _useState2[1];
  var isCompact = useMemo(function () {
    return isMobile || elementWidths && innerContainerWidth <= elementWidths;
  }, [isMobile, elementWidths, innerContainerWidth]);
  useEffect(function () {
    if (!isCompact) {
      var sum = headerWidth + bodyWidth + primaryButtonWidth + secondaryButtonWidth + ELEMENT_MARGINS;
      setElementWidths(sum);
    }
  }, [bodyWidth, elementWidths, isCompact, headerWidth, primaryButtonWidth, secondaryButtonWidth]);
  useEffect(function () {
    handleFullWidthPaddingChange(isMobile);
  }, [handleFullWidthPaddingChange, isMobile]);
  var shouldStackButtonsInMobileView = (color === 'default' || color === 'gray' || color === 'primary') && isMobile;
  var actionButtons = useMemo(function () {
    return /*#__PURE__*/_jsxs(Stack, {
      alignItems: "center",
      direction: "row",
      sx: {
        minWidth: 'fit-content',
        mt: 0
      },
      children: [/*#__PURE__*/_jsxs(Stack, {
        direction: shouldStackButtonsInMobileView ? 'column-reverse' : 'row',
        spacing: 3,
        width: shouldStackButtonsInMobileView ? '100%' : 'fit-content',
        children: [!hideSecondaryButton && /*#__PURE__*/_jsx(Button, {
          endIcon: secondaryButtonEndIcon,
          fullWidth: isMobile,
          onClick: onSecondaryButtonClick,
          ref: secondaryButtonRef,
          size: "small",
          startIcon: secondaryButtonStartIcon,
          sx: secondaryButtonStyles,
          variant: getButtonVariant('secondary', color),
          children: secondaryButtonContent
        }), !hidePrimaryButton && /*#__PURE__*/_jsx(Button, {
          endIcon: primaryButtonEndIcon,
          fullWidth: isMobile,
          onClick: onPrimaryButtonClick,
          ref: primaryButtonRef,
          size: "small",
          startIcon: primaryButtonStartIcon,
          sx: primaryButtonStyles,
          variant: getButtonVariant('primary', color),
          children: primaryButtonContent
        })]
      }), !hideXCloseButton && !isCompact && (_Box || (_Box = /*#__PURE__*/_jsx(Box, {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        ml: 2,
        children: closeButton
      })))]
    });
  }, [closeButton, color, hidePrimaryButton, hideSecondaryButton, hideXCloseButton, isCompact, isMobile, onPrimaryButtonClick, onSecondaryButtonClick, primaryButtonContent, primaryButtonEndIcon, primaryButtonStartIcon, primaryButtonStyles, secondaryButtonContent, secondaryButtonEndIcon, secondaryButtonStartIcon, secondaryButtonStyles, shouldStackButtonsInMobileView]);
  return /*#__PURE__*/_jsxs(Stack, {
    alignItems: "center",
    direction: 'row',
    m: "auto",
    maxWidth: CONTAINER_MAX_WIDTH,
    ref: innerContainerRef,
    "data-element": "Stack",
    "data-component": "FullWidthAlert",
    "data-source-file": "FullWidthAlert.tsx",
    children: [!isCompact && /*#__PURE__*/_jsx(Icon, {
      iconId: icon,
      sx: {
        mr: 4
      }
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: isCompact ? 'column' : 'row',
      width: "100%",
      "data-element": "Stack",
      "data-source-file": "FullWidthAlert.tsx",
      children: [/*#__PURE__*/_jsxs(Stack, {
        direction: "row",
        justifyContent: "space-between",
        "data-element": "Stack",
        "data-source-file": "FullWidthAlert.tsx",
        children: [header && /*#__PURE__*/_jsx(Typography, {
          color: "inherit",
          noWrap: !isCompact,
          ref: headerRef,
          sx: {
            minWidth: 'fit-content',
            mr: '6px',
            textOverflow: 'unset'
          },
          variant: "text-sm-semibold",
          children: header
        }), !hideXCloseButton && isCompact && /*#__PURE__*/_jsx(Box, {
          alignItems: "center",
          display: "flex",
          justifyContent: "start",
          ml: 2,
          children: closeButton
        })]
      }), body && /*#__PURE__*/_jsx(Typography, {
        color: "inherit",
        mr: 3,
        noWrap: !isCompact,
        ref: bodyRef,
        sx: {
          minWidth: 'fit-content',
          pb: (isCompact || isMobile) && (hidePrimaryButton !== true || hideSecondaryButton !== true) ? 4 : 0,
          textOverflow: 'unset'
        },
        variant: "text-sm-regular",
        children: body
      }), isCompact && actionButtons]
    }), !isCompact && actionButtons]
  });
}
var getButtonVariant = function getButtonVariant(buttonType, color) {
  if (color === 'gray' || color === 'default' || color === 'primary') {
    return buttonType === 'primary' ? 'primary' : 'secondary-gray';
  }
  return 'link';
};